import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Loader2,
  Clock,
  Search,
  Trash2,
  XCircle,
  X,
  AlertCircle,
  Download,
  Upload,
  Share2,
} from "lucide-react";
import ControlBar from "./controlBar";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import i18n from "../i18n";
import { API_BASE_URL } from "../config/api";

const Overview = ({ song }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [arSong, setArSong] = useState(null);
  const [transliteration, setTransliteration] = useState(null);
  const [translation, setTranslation] = useState(null);
  const [spanishTranslation, setSpanishTranslation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState({
    arabic: true,
    transliteration: true,
    english: true,
    spanish: true,
  });
  const [error, setError] = useState(null);
  const [activeVerseIndex, setActiveVerseIndex] = useState(-1);
  const [activeLineIndex, setActiveLineIndex] = useState(-1);
  const [activeChorusLineIndex, setActiveChorusLineIndex] = useState(-1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [screens, setScreens] = useState([
    {
      id: 1,
      displayOptions: {
        arabic: true,
        english: true,
        franco: true,
        spanish: true,
      },
      fontSizes: {
        arabic: 2.5,
        english: 2.0,
        franco: 1.8,
        spanish: 2.0,
      },
    },
  ]);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [isEndOfSong, setIsEndOfSong] = useState(false);
  const [languageDisplay, setLanguageDisplay] = useState({
    english: true,
    spanish: true,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState({
    partType: "",
    verseIndex: null,
    lineIndex: null,
    language: "arabic",
    note: "",
  });
  const [reportError, setReportError] = useState(null);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [historyMessage, setHistoryMessage] = useState(null);
  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const reportModalRef = useRef(null);
  const fileInputRef = useRef(null);

  const songID = song?.songID;
  const isRTL = i18n.language === "ar";

  // Highlight line based on URL params (for admin navigation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const partType = params.get("partType");
    const verseIndex = parseInt(params.get("verseIndex"), 10);
    const lineIndex = parseInt(params.get("lineIndex"), 10);

    if (partType && lineIndex >= 0 && arSong) {
      if (partType === "chorus") {
        setActiveChorusLineIndex(lineIndex);
        setActiveVerseIndex(-1);
        setActiveLineIndex(-1);
        setSelectedVerse({
          arabic: arSong?.chorus?.[lineIndex],
          english: translation?.chorus?.[lineIndex],
          franco: transliteration?.chorus?.[lineIndex],
          spanish: spanishTranslation?.chorus?.[lineIndex],
        });
      } else if (partType === "verse" && verseIndex >= 0) {
        setActiveVerseIndex(verseIndex);
        setActiveLineIndex(lineIndex);
        setActiveChorusLineIndex(-1);
        setSelectedVerse({
          arabic: arSong?.verses?.[verseIndex]?.[lineIndex],
          english: translation?.verses?.[verseIndex]?.[lineIndex],
          franco: transliteration?.verses?.[verseIndex]?.[lineIndex],
          spanish: spanishTranslation?.verses?.[verseIndex]?.[lineIndex],
        });
      }
    }
  }, [
    location.search,
    arSong,
    translation,
    transliteration,
    spanishTranslation,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load history from localStorage and add current song if not present
  useEffect(() => {
    const loadAndUpdateHistory = async () => {
      try {
        const storedHistory = localStorage.getItem("songHistory");
        let parsedHistory = storedHistory ? JSON.parse(storedHistory) : [];

        // Check if current song is in history
        const songExists = parsedHistory.some((item) => item.songID === songID);

        if (!songExists && songID) {
          try {
            const songRes = await axios.get(
              `${API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${songID}`
            );
            const songTitle = songRes.data.title || "Untitled Song";

            const newSong = {
              songID,
              title: songTitle,
              viewedAt: new Date().toISOString(),
            };

            parsedHistory = [newSong, ...parsedHistory].slice(0, 50);
            setHistory(parsedHistory);
            localStorage.setItem("songHistory", JSON.stringify(parsedHistory));
          } catch (error) {
            console.error("Error fetching song title for history:", error);
          }
        } else {
          setHistory(parsedHistory);
        }

        setFilteredHistory(parsedHistory);
      } catch (error) {
        console.error("Error managing song history:", error);
      }
    };

    loadAndUpdateHistory();
  }, [songID]);

  // Filter history based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredHistory(history);
    } else {
      const filtered = history.filter((song) =>
        song.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredHistory(filtered);
    }
  }, [searchTerm, history]);

  // Handle click outside to close history popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle click outside for report modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isReportModalOpen &&
        reportModalRef.current &&
        !reportModalRef.current.contains(event.target)
      ) {
        setIsReportModalOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isReportModalOpen]);

  // Export history to JSON file
  const exportHistory = () => {
    try {
      const dataStr = JSON.stringify(history, null, 2);
      const blob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `song_history_${new Date().toISOString().split("T")[0]
        }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setHistoryMessage({ type: "success", text: t("history.exportSuccess") });
      setTimeout(() => setHistoryMessage(null), 3000);
    } catch (error) {
      console.error("Error exporting history:", error);
      setHistoryMessage({ type: "error", text: t("history.exportError") });
      setTimeout(() => setHistoryMessage(null), 3000);
    }
  };

  // Trigger file input for import
  const triggerImport = () => {
    fileInputRef.current.click();
  };

  // Import history from JSON file
  const importHistory = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        if (!Array.isArray(importedData)) {
          throw new Error("Invalid history format");
        }

        // Validate each item
        const validData = importedData.filter(
          (item) => item.songID && item.title && item.viewedAt
        );

        // Merge with existing history, prioritizing newer viewedAt
        const mergedHistory = [...history];
        validData.forEach((newItem) => {
          const existingIndex = mergedHistory.findIndex(
            (item) => item.songID === newItem.songID
          );
          if (existingIndex !== -1) {
            if (
              new Date(newItem.viewedAt) >
              new Date(mergedHistory[existingIndex].viewedAt)
            ) {
              mergedHistory[existingIndex] = newItem;
            }
          } else {
            mergedHistory.push(newItem);
          }
        });

        // Sort by viewedAt (newest first) and limit to 50
        const sortedHistory = mergedHistory
          .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt))
          .slice(0, 50);

        setHistory(sortedHistory);
        setFilteredHistory(sortedHistory);
        localStorage.setItem("songHistory", JSON.stringify(sortedHistory));
        setHistoryMessage({
          type: "success",
          text: t("history.importSuccess"),
        });
        setTimeout(() => setHistoryMessage(null), 3000);
      } catch (error) {
        console.error("Error importing history:", error);
        setHistoryMessage({ type: "error", text: t("history.importError") });
        setTimeout(() => setHistoryMessage(null), 3000);
      }
    };
    reader.readAsText(file);
    event.target.value = null; // Reset file input
  };

  // Share history via link
  const shareHistory = () => {
    try {
      const dataStr = JSON.stringify(history);
      // Encode Unicode characters to handle non-Latin1 characters
      const encodedData = encodeURIComponent(dataStr);
      const latin1Data = unescape(encodedData);
      const base64Data = btoa(latin1Data);
      const shareUrl = `${window.location.origin}/import-history?data=${base64Data}`;
      navigator.clipboard.writeText(shareUrl);
      setHistoryMessage({ type: "success", text: t("history.shareSuccess") });
      setTimeout(() => setHistoryMessage(null), 3000);
    } catch (error) {
      console.error("Error sharing history:", error);
      setHistoryMessage({ type: "error", text: t("history.shareError") });
      setTimeout(() => setHistoryMessage(null), 3000);
    }
  };

  // Handle song selection
  const handleSelectSong = (songID) => {
    navigate(`/songs/${songID}`);
    setIsOpen(false);
  };

  // Delete a song from history
  const deleteSong = (e, songID) => {
    e.stopPropagation();
    const updatedHistory = history.filter((song) => song.songID !== songID);
    setHistory(updatedHistory);
    localStorage.setItem("songHistory", JSON.stringify(updatedHistory));
  };

  // Clear all history
  const clearHistory = () => {
    setHistory([]);
    setFilteredHistory([]);
    localStorage.removeItem("songHistory");
  };

  // Toggle language visibility
  const toggleLanguage = (lang) => {
    setLanguageDisplay((prev) => ({
      ...prev,
      [lang]: !prev[lang],
    }));
  };

  // Fetch song data
  useEffect(() => {
    if (songID == null) {
      setLoading(false);
      return;
    }

    const fetchSongs = async () => {
      setLoading(true);
      setLoadingStates({
        arabic: true,
        transliteration: true,
        english: true,
        spanish: true,
      });

      try {
        const arSongRes = await axios.get(
          `${API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${songID}`
        );
        setArSong(arSongRes.data);
        setLoadingStates((prev) => ({ ...prev, arabic: false }));

        try {
          const transliterationRes = await axios.get(
            `${API_BASE_URL}/api/FrankSongs/getFrankSongBySongID/${songID}`
          );
          setTransliteration(transliterationRes.data);
        } catch (error) {
          console.error("Error fetching transliteration:", error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, transliteration: false }));
        }

        try {
          const translationRes = await axios.get(
            `${API_BASE_URL}/api/ArbSongs/translateToEnglish/${songID}`
          );
          setTranslation(translationRes.data);
        } catch (error) {
          console.error("Error fetching English translation:", error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, english: false }));
        }

        try {
          const spanishTranslationRes = await axios.get(
            `${API_BASE_URL}/api/ArbSongs/translateToSpanish/${songID}`
          );
          setSpanishTranslation(spanishTranslationRes.data);
        } catch (error) {
          console.error("Error fetching Spanish translation:", error);
        } finally {
          setLoadingStates((prev) => ({ ...prev, spanish: false }));
        }
      } catch (error) {
        console.error("Error fetching song data:", error);
        setError(t("error.general"));
      } finally {
        setLoading(false);
      }
    };

    fetchSongs();
  }, [songID, t]);

  // Handle report modal open
  const openReportModal = (partType, verseIndex, lineIndex) => {
    setReportData({
      partType,
      verseIndex: partType === "verse" ? verseIndex : null,
      lineIndex,
      language: "arabic",
      note: "",
    });
    setReportError(null);
    setReportSuccess(false);
    setIsReportModalOpen(true);
  };

  // Handle report submission
  const submitReport = async () => {
    if (!reportData.note.trim()) {
      setReportError(t("report.errorNoteRequired"));
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/reports`,
        {
          songID,
          partType: reportData.partType,
          verseIndex: reportData.verseIndex,
          lineIndex: reportData.lineIndex,
          language: reportData.language,
          note: reportData.note,
        }
      );
      setReportSuccess(true);
      setTimeout(() => {
        setIsReportModalOpen(false);
        setReportSuccess(false);
      }, 1500);
    } catch (error) {
      console.error("Error submitting report:", error);
      setReportError(t("report.errorSubmission"));
    }
  };

  // Get total number of lines (verses + chorus)
  const getTotalLines = () => {
    let total = 0;
    if (Array.isArray(arSong?.chorus)) {
      total += arSong.chorus.length;
    }
    if (Array.isArray(arSong?.verses)) {
      arSong.verses.forEach((verse) => {
        total += verse.length;
      });
    }
    return total;
  };

  // Get current index considering all lines
  const getCurrentIndex = () => {
    if (isEndOfSong) {
      return getTotalLines();
    }

    let index = 0;

    if (activeChorusLineIndex !== -1) {
      return activeChorusLineIndex;
    }

    if (activeVerseIndex !== -1 && activeLineIndex !== -1) {
      if (Array.isArray(arSong?.chorus)) {
        index += arSong.chorus.length;
      }
      for (let i = 0; i < activeVerseIndex; i++) {
        index += arSong.verses[i].length;
      }
      index += activeLineIndex;
    }

    return index;
  };

  // Get line info based on global index
  const getLineInfo = (index, totalLines) => {
    if (index === totalLines) {
      return {
        type: "black",
        chorusIndex: -1,
        verseIndex: -1,
        lineIndex: -1,
      };
    }

    let currentIndex = 0;

    if (Array.isArray(arSong?.chorus)) {
      if (index < arSong.chorus.length) {
        return {
          type: "chorus",
          chorusIndex: index,
          verseIndex: -1,
          lineIndex: -1,
        };
      }
      currentIndex += arSong.chorus.length;
    }

    if (Array.isArray(arSong?.verses)) {
      for (
        let verseIndex = 0;
        verseIndex < arSong.verses.length;
        verseIndex++
      ) {
        const verse = arSong.verses[verseIndex];
        if (index < currentIndex + verse.length) {
          const lineIndex = index - currentIndex;
          return {
            type: "verse",
            chorusIndex: -1,
            verseIndex,
            lineIndex,
          };
        }
        currentIndex += verse.length;
      }
    }

    return null;
  };

  // Update selected verse based on line info
  const updateSelectedVerse = (lineInfo) => {
    if (!lineInfo) return;

    setIsEndOfSong(false);

    if (lineInfo.type === "black") {
      setIsEndOfSong(true);
      setSelectedVerse(null);
      setActiveChorusLineIndex(-1);
      setActiveVerseIndex(-1);
      setActiveLineIndex(-1);
      return;
    }

    if (lineInfo.type === "chorus") {
      setActiveChorusLineIndex(lineInfo.chorusIndex);
      setActiveVerseIndex(-1);
      setActiveLineIndex(-1);
      setSelectedVerse({
        arabic: arSong.chorus[lineInfo.chorusIndex],
        english: translation?.chorus?.[lineInfo.chorusIndex],
        franco: transliteration?.chorus?.[lineInfo.chorusIndex],
        spanish: spanishTranslation?.chorus?.[lineInfo.chorusIndex],
      });
    } else {
      setActiveChorusLineIndex(-1);
      setActiveVerseIndex(lineInfo.verseIndex);
      setActiveLineIndex(lineInfo.lineIndex);
      setSelectedVerse({
        arabic: arSong.verses[lineInfo.verseIndex][lineInfo.lineIndex],
        english:
          translation?.verses?.[lineInfo.verseIndex]?.[lineInfo.lineIndex],
        franco:
          transliteration?.verses?.[lineInfo.verseIndex]?.[lineInfo.lineIndex],
        spanish:
          spanishTranslation?.verses?.[lineInfo.verseIndex]?.[
          lineInfo.lineIndex
          ],
      });
    }
  };

  // Handle verse click
  const handleClick = (verseIndex, lineIndex) => {
    setActiveVerseIndex(verseIndex);
    setActiveLineIndex(lineIndex);
    setSelectedVerse({
      arabic: arSong.verses[verseIndex][lineIndex],
      english: translation?.verses[verseIndex][lineIndex],
      franco: transliteration?.verses[verseIndex][lineIndex],
      spanish: spanishTranslation?.verses[verseIndex][lineIndex],
    });
    setActiveChorusLineIndex(-1);
    setIsEndOfSong(false);
  };

  // Handle chorus click
  const handleChorusClick = (lineIndex) => {
    setActiveChorusLineIndex(lineIndex);
    setSelectedVerse({
      arabic: arSong.chorus[lineIndex],
      english: translation?.chorus[lineIndex],
      franco: transliteration?.chorus[lineIndex],
      spanish: spanishTranslation?.chorus[lineIndex],
    });
    setActiveVerseIndex(-1);
    setActiveLineIndex(-1);
    setIsEndOfSong(false);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!arSong) return;

      const totalLines = getTotalLines();
      const currentIndex = getCurrentIndex();

      let newIndex;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (isEndOfSong) {
          newIndex = 0;
        } else {
          newIndex = (currentIndex + 1) % (totalLines + 1);
        }
        const lineInfo = getLineInfo(newIndex, totalLines);
        updateSelectedVerse(lineInfo);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (currentIndex === 0) {
          newIndex = totalLines;
        } else {
          newIndex = (currentIndex - 1 + totalLines + 1) % (totalLines + 1);
        }
        const lineInfo = getLineInfo(newIndex, totalLines);
        updateSelectedVerse(lineInfo);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeChorusLineIndex !== -1) {
          handleChorusClick(activeChorusLineIndex);
        } else if (activeVerseIndex !== -1 && activeLineIndex !== -1) {
          handleClick(activeVerseIndex, activeLineIndex);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    arSong,
    activeVerseIndex,
    activeLineIndex,
    activeChorusLineIndex,
    isEndOfSong,
    translation,
    transliteration,
    spanishTranslation,
  ]);

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto p-2 sm:p-4 md:p-6 lg:p-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
          {/* Language toggle buttons skeleton */}
          <div className="mb-4 flex justify-end space-x-2">
            <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
            <div className="w-20 h-8 bg-gray-200 rounded-md"></div>
          </div>

          {/* Language headers skeleton */}
          <div className="grid grid-cols-4 gap-4 p-2 sm:p-3 md:p-4 bg-gray-50 border-b">
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto"></div>
          </div>

          {/* Chorus skeleton */}
          <div className="p-2 sm:p-3 md:p-4 border-b">
            <div className="mb-2 sm:mb-3 md:mb-4">
              <div className="inline-block bg-gray-200 h-6 w-20 rounded-full"></div>
            </div>
            {[...Array(3)].map((_, index) => (
              <div
                key={`chorus-skeleton-${index}`}
                className="flex items-center mb-4"
              >
                <div className="grid grid-cols-4 gap-4 flex-1">
                  <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                  <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                  <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                  <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                </div>
                <div className="ml-2 w-8 h-8 rounded-full bg-gray-200"></div>
              </div>
            ))}
          </div>

          {/* Verses skeleton */}
          {[...Array(2)].map((_, verseIndex) => (
            <div
              key={`verse-skeleton-${verseIndex}`}
              className="p-2 sm:p-3 md:p-4 border-b"
            >
              <div className="mb-2 sm:mb-3 md:mb-4">
                <div className="inline-block bg-gray-200 h-6 w-20 rounded-full"></div>
              </div>
              {[...Array(4)].map((_, lineIndex) => (
                <div
                  key={`verse-skeleton-${verseIndex}-${lineIndex}`}
                  className="flex items-center mb-4"
                >
                  <div className="grid grid-cols-4 gap-4 flex-1">
                    <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                    <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                    <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                    <div className="p-2 sm:p-3 md:p-4 rounded-lg bg-gray-200 h-12"></div>
                  </div>
                  <div className="ml-2 w-8 h-8 rounded-full bg-gray-200"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2 sm:p-3 md:p-4 text-red-500 text-center bg-red-50 rounded-lg text-sm sm:text-base">
        {error}
      </div>
    );
  }

  if (!arSong) {
    return (
      <div className="p-2 sm:p-3 md:p-4 text-gray-500 text-center text-sm sm:text-base">
        {t("noSongSelected")}
      </div>
    );
  }

  const getGridColsClass = () => {
    if (isMobile) {
      return "grid grid-cols-1 gap-2";
    }
    const visibleCols =
      2 + (languageDisplay.english ? 1 : 0) + (languageDisplay.spanish ? 1 : 0);
    switch (visibleCols) {
      case 4:
        return "grid grid-cols-4 gap-4";
      case 3:
        return "grid grid-cols-3 gap-4";
      case 2:
        return "grid grid-cols-2 gap-4";
      default:
        return "grid grid-cols-4 gap-4";
    }
  };

  const getHeaderGridColsClass = () => {
    const visibleCols =
      2 + (languageDisplay.english ? 1 : 0) + (languageDisplay.spanish ? 1 : 0);
    switch (visibleCols) {
      case 4:
        return "grid grid-cols-4 gap-4";
      case 3:
        return "grid grid-cols-3 gap-4";
      case 2:
        return "grid grid-cols-2 gap-4";
      default:
        return "grid grid-cols-4 gap-4";
    }
  };

  const gridColsClass = getGridColsClass();

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`w-full max-w-7xl mx-auto p-2 sm:p-4 md:p-6 lg:p-8 ${isRTL ? "font-[Tajawal]" : ""
        }`}
      style={isRTL ? { fontFamily: "Tajawal, Amiri, Arial, sans-serif" } : {}}
    >
      {/* Language toggle buttons */}
      <div
        className={`mb-4 flex gap-2 justify-end ${isRTL ? "flex-row-reverse space-x-reverse" : ""
          }`}
      >
        <button
          onClick={() => toggleLanguage("english")}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${languageDisplay.english
            ? "bg-orange-500 text-white"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
        >
          {t("display.english")}
        </button>
        <button
          onClick={() => toggleLanguage("spanish")}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${languageDisplay.spanish
            ? "bg-orange-500 text-white"
            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
        >
          {t("display.spanish")}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div
          className={`${getHeaderGridColsClass()} p-2 sm:p-3 md:p-4 bg-gray-50 border-b`}
        >
          <div
            className={`text-center font-semibold text-gray-700 text-sm sm:text-base ${isRTL ? "font-[Tajawal]" : ""
              }`}
          >
            {t("display.arabic")}
          </div>
          <div className="text-center font-semibold text-gray-700 text-sm sm:text-base">
            {t("display.franco")}
          </div>
          {languageDisplay.english && (
            <div className="text-center font-semibold text-gray-700 text-sm sm:text-base">
              {t("display.english")}
            </div>
          )}
          {languageDisplay.spanish && (
            <div className="text-center font-semibold text-gray-700 text-sm sm:text-base">
              {t("display.spanish")}
            </div>
          )}
        </div>

        <div className="divide-y divide-gray-100">
          {arSong.chorus && arSong.chorus.length > 0 && (
            <div className="p-2 sm:p-3 md:p-4">
              {arSong.chorus.map((line, index) => (
                <div
                  key={`chorus-${index}`}
                  className={`mb-4 ${isMobile ? "flex flex-col" : "flex items-center"
                    } ${isRTL ? "" : ""}`}
                >
                  <div className={`${gridColsClass} flex-1 items-stretch`}>
                    <div
                      className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeChorusLineIndex === index
                        ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                        : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                        } ${isRTL ? "text-right font-[Tajawal]" : ""}`}
                      onClick={() => handleChorusClick(index)}
                    >
                      <p className="text-sm sm:text-base md:text-lg text-gray-800 text-right font-bold break-words w-full">
                        {line}
                      </p>
                    </div>
                    <div
                      className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeChorusLineIndex === index
                        ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                        : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                        } ${isRTL ? "text-right font-[Tajawal]" : ""}`}
                      onClick={() => handleChorusClick(index)}
                    >
                      {loadingStates.transliteration ? (
                        <div className="flex justify-center items-center h-6 w-full">
                          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        </div>
                      ) : (
                        <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                          {transliteration?.chorus?.[index] || ""}
                        </p>
                      )}
                    </div>
                    {languageDisplay.english && (
                      <div
                        className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeChorusLineIndex === index
                          ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                          : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                          } ${isRTL ? "text-right font-[Tajawal]" : ""}`}
                        onClick={() => handleChorusClick(index)}
                      >
                        {loadingStates.english ? (
                          <div className="flex justify-center items-center h-6 w-full">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          </div>
                        ) : (
                          <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                            {translation?.chorus?.[index] || ""}
                          </p>
                        )}
                      </div>
                    )}
                    {languageDisplay.spanish && (
                      <div
                        className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeChorusLineIndex === index
                          ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                          : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                          } ${isRTL ? "text-right font-[Tajawal]" : ""}`}
                        onClick={() => handleChorusClick(index)}
                      >
                        {loadingStates.spanish ? (
                          <div className="flex justify-center items-center h-6 w-full">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          </div>
                        ) : (
                          <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                            {spanishTranslation?.chorus?.[index] || ""}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => openReportModal("chorus", null, index)}
                    className={`p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors shrink-0 ${isMobile ? "self-end mt-2" : "ml-2"
                      }`}
                    title={t("report.submit")}
                    aria-label={
                      t("report.submit") + " for chorus line " + (index + 1)
                    }
                  >
                    <AlertCircle size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {arSong.verses.map((verse, verseIndex) => (
            <div key={`verse-${verseIndex}`} className="p-2 sm:p-3 md:p-4">
              <div className="mb-2 sm:mb-3 md:mb-4">
                <span className="inline-block bg-gray-100 text-gray-600 text-xs sm:text-sm px-2 py-1 rounded-full">
                  Verse {verseIndex + 1}
                </span>
              </div>
              {verse.map((line, lineIndex) => (
                <div
                  key={`verse-${verseIndex}-line-${lineIndex}`}
                  className={`mb-4 ${isMobile ? "flex flex-col" : "flex items-center"
                    }`}
                >
                  <div className={`${gridColsClass} flex-1 items-stretch`}>
                    <div
                      className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeVerseIndex === verseIndex &&
                        activeLineIndex === lineIndex
                        ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                        : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                        }`}
                      onClick={() => handleClick(verseIndex, lineIndex)}
                    >
                      <p className="text-sm sm:text-base md:text-lg text-gray-800 text-right font-bold break-words w-full">
                        {line}
                      </p>
                    </div>
                    <div
                      className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeVerseIndex === verseIndex &&
                        activeLineIndex === lineIndex
                        ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                        : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                        }`}
                      onClick={() => handleClick(verseIndex, lineIndex)}
                    >
                      {loadingStates.transliteration ? (
                        <div className="flex justify-center items-center h-6 w-full">
                          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                        </div>
                      ) : (
                        <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                          {transliteration?.verses?.[verseIndex]?.[lineIndex] ||
                            ""}
                        </p>
                      )}
                    </div>
                    {languageDisplay.english && (
                      <div
                        className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeVerseIndex === verseIndex &&
                          activeLineIndex === lineIndex
                          ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                          : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                          }`}
                        onClick={() => handleClick(verseIndex, lineIndex)}
                      >
                        {loadingStates.english ? (
                          <div className="flex justify-center items-center h-6 w-full">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          </div>
                        ) : (
                          <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                            {translation?.verses?.[verseIndex]?.[lineIndex] ||
                              ""}
                          </p>
                        )}
                      </div>
                    )}
                    {languageDisplay.spanish && (
                      <div
                        className={`p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer flex items-center ${activeVerseIndex === verseIndex &&
                          activeLineIndex === lineIndex
                          ? "bg-orange-100 shadow-md scale-[1.02] z-10"
                          : "hover:bg-gray-50 hover:shadow-lg hover:scale-[1.02] hover:z-10"
                          }`}
                        onClick={() => handleClick(verseIndex, lineIndex)}
                      >
                        {loadingStates.spanish ? (
                          <div className="flex justify-center items-center h-6 w-full">
                            <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                          </div>
                        ) : (
                          <p className="text-sm sm:text-base md:text-lg text-gray-800 text-center break-words w-full">
                            {spanishTranslation?.verses?.[verseIndex]?.[
                              lineIndex
                            ] || ""}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      openReportModal("verse", verseIndex, lineIndex)
                    }
                    className={`p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors shrink-0 ${isMobile ? "self-end mt-2" : "ml-2"
                      }`}
                    title={t("report.submit")}
                    aria-label={
                      t("report.submit") +
                      " for verse " +
                      (verseIndex + 1) +
                      " line " +
                      (lineIndex + 1)
                    }
                  >
                    <AlertCircle size={16} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Report Modal */}
      {isReportModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div
            ref={reportModalRef}
            className="bg-white rounded-xl p-6 w-full max-w-md shadow-lg"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{t("report.title")}</h2>
              <button
                onClick={() => setIsReportModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("report.language")}
              </label>
              <select
                value={reportData.language}
                onChange={(e) =>
                  setReportData({ ...reportData, language: e.target.value })
                }
                className="w-full p-2 border rounded-md focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="arabic">{t("display.arabic")}</option>
                <option value="franco">{t("display.franco")}</option>
                <option value="english">{t("display.english")}</option>
                <option value="spanish">{t("display.spanish")}</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("report.note")}
              </label>
              <textarea
                value={reportData.note}
                onChange={(e) =>
                  setReportData({ ...reportData, note: e.target.value })
                }
                className="w-full p-2 border rounded-md focus:ring-orange-500 focus:border-orange-500"
                rows={4}
                maxLength={500}
                placeholder={t("report.notePlaceholder")}
              />
            </div>
            {reportError && (
              <p className="text-red-500 text-sm mb-4">{reportError}</p>
            )}
            {reportSuccess && (
              <p className="text-green-500 text-sm mb-4">
                {t("report.success")}
              </p>
            )}
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsReportModalOpen(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {t("report.cancel")}
              </button>
              <button
                onClick={submitReport}
                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
                disabled={reportSuccess}
              >
                {t("report.submit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History popup */}
      <div
        className={`fixed ${i18n.language === "ar" ? "left-0 bottom-0" : "right-0 bottom-0"
          } z-50 flex flex-col items-end`}
      >
        <div
          className={`relative ${i18n.language === "ar" ? "pl-4" : "pr-4"
            } pb-4`}
        >
          {!isOpen && (
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(true)}
              className={`fixed ${i18n.language === "ar"
                ? "left-0 bottom-80 rounded-xl rounded-tl-none rounded-bl-none"
                : "right-0 bottom-80 rounded-xl rounded-tr-none rounded-br-none"
                }  ${isMobile ? "w-10" : "w-24"} h-10 flex items-center justify-start px-3 gap-2 bg-[#333] hover:bg-black transition-colors`}
            >
              <Clock className="text-white/90" size={16} />
              {!isMobile && (
                <span className="text-white/90 text-sm">
                  {t("history.history")}
                </span>
              )}
            </button>
          )}

          <div
            ref={popupRef}
            className={`bg-black rounded-xl shadow-lg outline outline-1 outline-white/40 backdrop-blur-md transition-all duration-300 ease-in-out
    ${isOpen
                ? `opacity-100 ${isMobile
                  ? "min-w-[90vw] max-w-[90vw] max-h-[50vh]"
                  : "min-w-[615px] max-w-[615px] max-h-[70vh]"
                } flex flex-col`
                : "opacity-0 w-0 max-h-0"
              }`}
          >
            <div className="flex flex-col min-h-0">
              <div className="flex items-center justify-between p-3 border-b border-white/20">
                <h2 className="text-white/80 text-sm flex items-center gap-2">
                  <Clock size={16} />
                  {t("history.title")}
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/10"
                >
                  <X className="text-white/80" size={14} />
                </button>
              </div>

              <div className="p-3 border-b border-white/20">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={t("history.search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-8 py-1.5 bg-white/10 text-white/80 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
                  />
                  <Search
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/60"
                    size={14}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80"
                    >
                      <XCircle size={14} />
                    </button>
                  )}
                </div>
              </div>

              {historyMessage && (
                <div
                  className={`p-2 text-sm text-center ${historyMessage.type === "success"
                    ? "bg-green-500/20 text-green-400"
                    : "bg-red-500/20 text-red-400"
                    }`}
                >
                  {historyMessage.text}
                </div>
              )}

              <div className="flex-1 overflow-y-auto">
                {filteredHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-4 text-white/60">
                    <Clock
                      size={32}
                      strokeWidth={1}
                      className="mb-2 opacity-50"
                    />
                    <p className="text-center text-sm">
                      {searchTerm
                        ? t("history.noSearchResults")
                        : t("history.empty")}
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-white/20">
                    {filteredHistory.map((song) => (
                      <li
                        key={song.songID}
                        onClick={() => handleSelectSong(song.songID)}
                        className="p-3 hover:bg-white/10 cursor-pointer transition-colors flex items-center justify-between"
                      >
                        <div className="flex-1 pr-2">
                          <p className="text-white/80 text-sm truncate">
                            {song.title}
                          </p>
                          <p className="text-white/60 text-xs">
                            {new Date(song.viewedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) => deleteSong(e, song.songID)}
                          className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/10 text-white/60 hover:text-red-500"
                          title={t("history.removeSong")}
                        >
                          <Trash2 size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div
                className={`p-3 border-t border-white/20 bg-black/50 sticky bottom-0 flex gap-2`}
              >
                <button
                  onClick={exportHistory}
                  className="flex-1 flex items-center justify-center p-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-md transition-colors"
                  title={t("history.export")} // Always show tooltip for accessibility
                >
                  <Download size={14} />
                  {!isMobile && <span className="ml-2 text-sm">{t("history.export")}</span>}
                </button>
                <button
                  onClick={triggerImport}
                  className="flex-1 flex items-center justify-center p-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-md transition-colors"
                  title={t("history.import")} // Always show tooltip for accessibility
                >
                  <Upload size={14} />
                  {!isMobile && <span className="ml-2 text-sm">{t("history.import")}</span>}
                </button>
                <button
                  onClick={shareHistory}
                  className="flex-1 flex items-center justify-center p-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 rounded-md transition-colors"
                  title={t("history.share")} // Always show tooltip for accessibility
                >
                  <Share2 size={14} />
                  {!isMobile && <span className="ml-2 text-sm">{t("history.share")}</span>}
                </button>
                <button
                  onClick={clearHistory}
                  className="flex-1 flex items-center justify-center p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-md transition-colors"
                  title={t("history.clearAll")} // Always show tooltip for accessibility
                >
                  <Trash2 size={14} />
                  {!isMobile && <span className="ml-2 text-sm">{t("history.clearAll")}</span>}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={importHistory}
                  accept=".json"
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {(selectedVerse || isEndOfSong) && !isMobile && (
          <ControlBar
            selectedVerse={selectedVerse}
            screens={screens}
            setScreens={setScreens}
            isEndOfSong={isEndOfSong}
          />
        )}
      </div>
    </div>
  );
};

export default Overview;
